import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Flame, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setSessionFromToken } = useAuth();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing reset token");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return;
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      const r = await api.post("/auth/reset-password", { token, password });
      // Log the user in immediately with the freshly issued JWT.
      if (r.data?.access_token && r.data?.user) {
        await setSessionFromToken(r.data.access_token, r.data.user);
      }
      toast.success("Password updated");
      const u = r.data?.user;
      if (u?.is_coach) navigate("/coach-dashboard", { replace: true });
      else if (u && !u.onboarded) navigate("/onboarding", { replace: true });
      else navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian-900 flex flex-col">
      <header className="border-b border-obsidian-600 px-6 py-4">
        <Link to="/" className="flex items-center gap-2">
          <img src="/icon-192.png" alt="Ripple Effect" className="h-6 w-6 rounded-sm" />
          <span className="font-heading text-xl">RIPPLE<span className="text-blaze"> EFFECT</span></span>
        </Link>
      </header>
      <main className="flex-1 grid place-items-center px-5 py-10">
        <form onSubmit={submit} className="w-full max-w-md obs-card" data-testid="reset-password-card">
          <div className="overline text-blaze mb-2">/ New password</div>
          <h1 className="font-heading text-4xl uppercase mb-3">Set it.</h1>
          <p className="text-zinc-400 text-sm mb-6">
            Choose a new password. You'll be signed in automatically.
          </p>
          <label className="block mb-4">
            <div className="overline mb-1">New password</div>
            <input
              data-testid="reset-password-new"
              type="password"
              required
              minLength={6}
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
            />
          </label>
          <label className="block mb-6">
            <div className="overline mb-1">Confirm password</div>
            <input
              data-testid="reset-password-confirm"
              type="password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2.5 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
            />
          </label>
          <button
            data-testid="reset-password-submit"
            disabled={loading || !token}
            className="blaze-btn w-full py-3 text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            <Flame className="h-4 w-4" /> {loading ? "Updating…" : "Update password"}
          </button>
          <p className="text-[11px] text-zinc-500 mt-3 uppercase tracking-widest flex items-center gap-2">
            <ShieldCheck className="h-3 w-3" /> One-time link · expires after use
          </p>
          <div className="mt-6 text-sm text-zinc-400 text-center">
            <Link to="/auth/login" data-testid="reset-password-to-login" className="text-blaze hover:text-blaze-hover">
              Back to sign in
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
